import { useState } from 'react';
import WizardCard from './WizardCard.jsx';

// Three slots. Each slot can hold either a referenced task (text + task_id)
// or free-written text (text only, task_id null). Carry-forward suggestions
// from yesterday surface as chips; tapping a chip fills the slot's text.
export default function StepMITs({
  briefing,
  topTasks,
  carriedForward,
  onSave,
  onBack,
  onNext,
}) {
  // Local mirror so the input feels responsive; we save on Next.
  const [slots, setSlots] = useState(() => [1, 2, 3].map((i) => ({
    text: briefing?.[`mit_${i}_text`] ?? '',
    task_id: briefing?.[`mit_${i}_task_id`] ?? null,
  })));

  function updateSlot(idx, patch) {
    setSlots((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }

  function pickTask(idx, task) {
    updateSlot(idx, { text: task.title, task_id: task.id });
  }

  function pickCarryForward(idx, text) {
    // Carry-forward chips are plain text; no task_id available unless we look it up.
    updateSlot(idx, { text, task_id: null });
  }

  function clearSlot(idx) {
    updateSlot(idx, { text: '', task_id: null });
  }

  const allFilled = slots.every((s) => s.text.trim().length > 0);

  function handleNext() {
    const patch = {};
    slots.forEach((s, i) => {
      patch[`mit_${i + 1}_text`] = s.text.trim();
      patch[`mit_${i + 1}_task_id`] = s.task_id;
    });
    onSave(patch);
    onNext();
  }

  return (
    <WizardCard
      step={5}
      title="Your three most important tasks today"
      subtitle="If you only did these three, the day would be a win."
      onBack={onBack}
      onNext={handleNext}
      nextDisabled={!allFilled}
    >
      <div className="space-y-5">
        {slots.map((slot, idx) => (
          <MITSlot
            key={idx}
            idx={idx}
            slot={slot}
            topTasks={topTasks}
            carriedForward={carriedForward}
            onTextChange={(text) =>
              updateSlot(idx, { text, task_id: null })
            }
            onPickTask={(t) => pickTask(idx, t)}
            onPickCarry={(t) => pickCarryForward(idx, t)}
            onClear={() => clearSlot(idx)}
          />
        ))}
      </div>
    </WizardCard>
  );
}

function MITSlot({
  idx,
  slot,
  topTasks,
  carriedForward,
  onTextChange,
  onPickTask,
  onPickCarry,
  onClear,
}) {
  const [showSuggestions, setShowSuggestions] = useState(!slot.text);

  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-text-secondary mb-2">
        MIT {idx + 1}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={slot.text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder="What matters most…"
          onFocus={() => setShowSuggestions(true)}
          className="flex-1 px-3 py-2 bg-bg-input border border-border-base rounded text-text-primary focus:outline-none focus:border-accent-blue"
        />
        {slot.text && (
          <button
            onClick={onClear}
            className="text-xs text-text-muted hover:text-accent-red transition"
            title="Clear"
          >
            ✕
          </button>
        )}
      </div>

      {showSuggestions && (
        <div className="mt-2 space-y-2">
          {carriedForward.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-text-muted mb-1">
                Carried forward from yesterday
              </div>
              <div className="flex flex-wrap gap-1.5">
                {carriedForward.map((cf, i) => (
                  <Chip key={`cf-${i}`} onClick={() => onPickCarry(cf)}>
                    {cf}
                  </Chip>
                ))}
              </div>
            </div>
          )}
          {topTasks.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-text-muted mb-1">
                Open tasks
              </div>
              <div className="flex flex-wrap gap-1.5">
                {topTasks.map((t) => (
                  <Chip
                    key={t.id}
                    priority={t.priority}
                    onClick={() => onPickTask(t)}
                  >
                    {t.title}
                  </Chip>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Chip({ priority, onClick, children }) {
  const ring =
    priority === 'high'
      ? 'border-accent-amber/50'
      : 'border-border-base';
  return (
    <button
      onClick={onClick}
      className={`text-xs px-2 py-1 bg-bg-input border ${ring} rounded text-text-primary hover:bg-bg-card-hover transition`}
    >
      {children}
    </button>
  );
}
